import { RenderLayer, WorldObjectType } from "../api";
import { Globe } from "./Globe";
import { Color as ThreeColor, CubeTexture, Line, Mesh, Points, Scene, Texture } from "three";
import { DrawableObject, PanoramaObject } from "./objects";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial";
/**
 * Implementation of IWorld
 */
export class World {
    constructor(renderer) {
        this.objects = new Set();
        this.panos = new Set();
        this.lastScene = null;
        this.background = new ThreeColor(0x000000);
        this.renderer = renderer;
        this.globe = new Globe(renderer);
    }
    addPanorama(panorama) {
        if (panorama instanceof PanoramaObject) {
            this.panos.add(panorama);
        }
        else {
            throw new Error("Unsupported IPanorama implementation");
        }
    }
    removePanorama(panorama) {
        if (panorama instanceof PanoramaObject) {
            this.panos.delete(panorama);
        }
        else {
            throw new Error("Unsupported IPanorama implementation");
        }
    }
    addObject(object) {
        this.objects.add(object);
    }
    removeObject(object) {
        this.objects.delete(object);
    }
    getGlobe() {
        return this.globe;
    }
    getCamera() {
        return this.renderer.camera;
    }
    getObjects() {
        return this.objects;
    }
    setBackgroundColor(color) {
        if (this.background instanceof Texture || this.background instanceof CubeTexture) {
            this.background.dispose();
        }
        this.background = new ThreeColor(color.r, color.g, color.b);
    }
    prepareObject(obj) {
        const { x, y } = this.renderer.getSize();
        obj.object.traverse((o) => {
            if (o instanceof Mesh || o instanceof Line || o instanceof Points) {
                const material = o.material;
                if (Array.isArray(material)) {
                    for (const m of material) {
                        if (m instanceof LineMaterial) {
                            m.resolution.set(x, y);
                        }
                    }
                }
                else if (material instanceof LineMaterial) {
                    material.resolution.set(x, y);
                }
            }
        });
    }
    /**
     * Transient scene that should be built every frame and used once for rendering
     */
    buildScene() {
        // New one-time-use scene for this frame
        const beforePanoScene = new Scene();
        const beforePanoTransparentScene = new Scene();
        const beforePanoKmlScene = new Scene();
        const panoScene = new Scene();
        const afterPanoScene = new Scene();
        const afterPanoTransparentScene = new Scene();
        const afterPanoKmlScene = new Scene();
        this.lastScene = new Scene();
        // Check if the globe needs to be rebuilt with new layers
        this.globe.checkLayers();
        // Add the globe, if one has been built
        if (this.globe.inner !== undefined && this.globe.visible) {
            beforePanoScene.add(this.globe.inner);
        }
        // Set the background
        beforePanoScene.background = this.background;
        // Add all our objects to the scene
        for (const obj of this.objects) {
            if (obj instanceof DrawableObject && obj.visible) {
                this.prepareObject(obj);
                switch (obj.getRenderLayer()) {
                    case RenderLayer.Main:
                        if (obj.getObjectType() === WorldObjectType.KmlObject) {
                            beforePanoKmlScene.add(obj.object);
                        }
                        else {
                            if (obj.isTransparent) {
                                beforePanoTransparentScene.add(obj.object);
                            }
                            else {
                                beforePanoScene.add(obj.object);
                            }
                        }
                        break;
                    case RenderLayer.AfterPanorama:
                        if (obj.getObjectType() === WorldObjectType.KmlObject) {
                            afterPanoKmlScene.add(obj.object);
                        }
                        else {
                            if (obj.isTransparent) {
                                afterPanoTransparentScene.add(obj.object);
                            }
                            else {
                                afterPanoScene.add(obj.object);
                            }
                        }
                        break;
                }
            }
        }
        // Add all our panoramas to the scene
        for (const pano of this.panos) {
            if (pano.visible) {
                panoScene.add(pano.object);
            }
        }
        // Add the main object scenes to the 'lastScene' field so we can ray-cast against the last
        this.lastScene.add(beforePanoScene, beforePanoTransparentScene, afterPanoScene, afterPanoTransparentScene);
        return [
            beforePanoScene,
            beforePanoTransparentScene,
            beforePanoKmlScene,
            panoScene,
            afterPanoScene,
            afterPanoTransparentScene,
            afterPanoKmlScene,
        ];
    }
}
